import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, ArrowRight, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 15;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | expired | failed
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(data);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (['expired', 'failed'].includes(data.payment_status)) { setState('failed'); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState('expired'); return; }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (!cancelled) { setState('failed'); }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const dollars = record?.amount ? (record.amount / 100).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />
      <div className="max-w-lg mx-auto px-5 pt-16 pb-24 text-center">
        <div className="bg-[#101014] border border-[#1e1e26] rounded-3xl p-10">
          {state === 'polling' && (
            <>
              <Loader2 className="w-12 h-12 text-[#ea9a6f] mx-auto animate-spin mb-5" />
              <h1 className="serif text-3xl text-white mb-2">Confirming payment…</h1>
              <p className="text-[#8a877f]">Talking to Stripe. This usually takes a couple of seconds.</p>
            </>
          )}
          {state === 'paid' && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#7dd4c5]/15 border border-[#7dd4c5]/40 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-[#7dd4c5]" />
              </div>
              <h1 className="serif text-4xl text-white mb-2">You’re in.</h1>
              <p className="text-[#a8a49b]">Welcome to CrewFace {record?.lookup_key === 'crewface_site_monthly' ? 'Site' : 'Crew'}.{dollars && ` ${record.currency?.toUpperCase()} $${dollars} charged.`}</p>
              <div className="mono text-[10px] text-[#5a5850] mt-4 break-all">session {sessionId}</div>
              <Link to="/app"><Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full h-11 px-6 mt-6">Go to dashboard <ArrowRight className="ml-1.5 w-4 h-4" /></Button></Link>
            </>
          )}
          {state === 'expired' && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#ea9a6f]/15 border border-[#ea9a6f]/40 flex items-center justify-center mx-auto mb-5">
                <Loader2 className="w-7 h-7 text-[#ea9a6f]" />
              </div>
              <h1 className="serif text-3xl text-white mb-2">Still processing</h1>
              <p className="text-[#a8a49b]">Stripe is taking longer than usual. Your account will unlock automatically once confirmed — check back in a minute.</p>
              <Link to="/pricing"><Button variant="outline" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-11 px-6 mt-6">Back to pricing</Button></Link>
            </>
          )}
          {state === 'failed' && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mx-auto mb-5">
                <XCircle className="w-7 h-7 text-red-400" />
              </div>
              <h1 className="serif text-3xl text-white mb-2">Payment didn’t complete</h1>
              <p className="text-[#a8a49b]">No charge was made. You can try again anytime.</p>
              <Link to="/pricing"><Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full h-11 px-6 mt-6">Back to pricing</Button></Link>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
